import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import {
  SearchSessionsRequest,
  FetchSessionMarkersRequest,
  SearchServicePublic as svc,
  SearchRequest,
} from '@fullstory/proto/fs/services/alexa';
import { SearchSessionsTypes } from './types';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { Event, SegmentDefinition, TimeRange } from '@fullstory/proto/fs/api/segment';
import { isArray } from 'lodash';
import { isSegmentDefinition } from '@fullstory/search-shared';
import { assertExhaustive } from '@fullstory/typeutils';
import { SearchSessionsPayload } from '../matchingUsers/actions';
import { int64 } from '@fullstory/proto/google/protobuf';
import jstz from 'jstz';

const isEventSearch = (payload: SearchSessionsPayload): payload is EventSearch =>
  (payload as EventSearch).matchingBehaviors !== undefined;
const isSegmentDefinitions = (payload: SearchSessionsPayload): payload is SegmentDefinition[] =>
  isArray(payload) && (payload as SegmentDefinition[]).every(isSegmentDefinition);

export const alexaGrpcTimeoutHeader = { 'GRPC-Timeout': '1M' };

export const SearchSessionsRequestActions = {
  searchSessions: (
    payload: SearchSessionsPayload,
    indvId: string,
    orgId: string,
    batchOptions?: {
      maxOutgoingRequests: number;
      id: string;
    },
    force?: boolean,
  ) => {
    let segmentDefinition: SegmentDefinition | null = null;
    let eventSearch: EventSearch | null = null;
    let additionalSegmentDefinitions: SegmentDefinition[] = [];
    if (isEventSearch(payload)) {
      eventSearch = payload;
    } else if (isSegmentDefinitions(payload)) {
      segmentDefinition = payload[0] ?? null;
      additionalSegmentDefinitions = payload.slice(1);
    } else {
      assertExhaustive(payload);
    }
    const req = createSearchSessionsRequest(
      segmentDefinition,
      eventSearch,
      additionalSegmentDefinitions,
      orgId,
      indvId,
    );

    return createGRPCAction<typeof svc.SearchSessions>()(
      {
        type: SearchSessionsTypes.SearchSessionsRequest,
        grpcFunction: svc.SearchSessions,
        body: req,
        settings: {
          batchOptions,
          headers: alexaGrpcTimeoutHeader,
        },
        readWrite: 'write',
        force,
      },
      SearchSessionsTypes.SearchSessionsSuccess,
      SearchSessionsTypes.SearchSessionsFailure,
    );
  },
  fetchSessionMarkers: (
    orgId: string,
    segmentDefinition: SegmentDefinition,
    indvId: string,
    userId: string,
    sessionId: string,
    force?: boolean,
    eventSearch: EventSearch | null = null,
  ) =>
    createGRPCAction<typeof svc.FetchSessionMarkers>()(
      {
        type: SearchSessionsTypes.SessionMarkersRequest,
        grpcFunction: svc.FetchSessionMarkers,
        body: createSessionMarkersRequest(
          orgId,
          segmentDefinition,
          indvId,
          userId,
          sessionId,
          eventSearch,
        ),
        settings: {
          headers: alexaGrpcTimeoutHeader,
        },
        force,
        readWrite: 'write',
      },
      SearchSessionsTypes.SessionMarkersSuccess,
      SearchSessionsTypes.SessionMarkersFailure,
    ),
  searchPreviews: (orgId: string, event: Event | null, timeRange: TimeRange, limit: number) =>
    createGRPCAction<typeof svc.SearchPreviews>()(
      {
        type: SearchSessionsTypes.SearchPreviewsRequest,
        grpcFunction: svc.SearchPreviews,
        body: {
          orgId,
          event,
          timeRange,
          limit,
          now: null,
          timezone: jstz.determine().name(),
          cacheOptions: null,
          additionalFields: [],
        },
        settings: {
          headers: alexaGrpcTimeoutHeader,
        },
        readWrite: 'read',
      },
      SearchSessionsTypes.SearchPreviewsSuccess,
      SearchSessionsTypes.SearchPreviewsFailure,
    ),
};

const createSearchSessionsRequest = (
  segmentDefinition: SegmentDefinition | null,
  eventSearch: EventSearch | null,
  additionalSegmentDefinitions: SegmentDefinition[],
  orgId: string,
  indvId: string,
): SearchSessionsRequest => ({
  orgId,
  searchRequest: createSearchRequest(segmentDefinition, eventSearch, indvId, '0', '0', 1),
  additionalSegmentDefinitions,
  cacheOptions: null,
});

const createSessionMarkersRequest = (
  orgId: string,
  segmentDefinition: SegmentDefinition,
  indvId: string,
  userId: string,
  sessionId: string,
  eventSearch: EventSearch | null = null,
): FetchSessionMarkersRequest => ({
  orgId,
  searchRequest: createSearchRequest(segmentDefinition, eventSearch, indvId, userId, sessionId, 0),
});

const createSearchRequest = (
  segmentDefinition: SegmentDefinition | null,
  eventSearch: EventSearch | null,
  indvId: string,
  userId: string,
  sessionId: string,
  limit: number,
): SearchRequest => ({
  segmentDefinition,
  eventSearch,
  indvId: indvId as int64,
  userId: userId as int64,
  sessionId: sessionId as int64,
  start: 0,
  limit,
  now: null,
  timezone: jstz.determine().name(),
});

export type SearchSessionsActions = RequestActionType<typeof SearchSessionsRequestActions>;
